import type { ListingPaymentPollingTimer } from "./listingPaymentPolling";

export const STALE_PAYMENT_CONFIGURATION_MESSAGE =
  "Payment details changed. Refresh or reload payment details before trying again.";

export class LocalListingPaymentInitiationTimeoutError extends Error {
  constructor() {
    super("The payment initiation request timed out locally.");
    this.name = "LocalListingPaymentInitiationTimeoutError";
  }
}

export function withListingPaymentInitiationTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new LocalListingPaymentInitiationTimeoutError()),
      timeoutMs
    );

    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

export type ListingPaymentInitiationErrorDisposition =
  | "stale_configuration"
  | "deterministic_rejection"
  | "recover_authoritatively";

export type ListingPaymentInitiationErrorUiState =
  | "configuration_error"
  | "initiation_rejected"
  | "authentication_required"
  | "permission_denied"
  | "initiation_error";

export type ListingPaymentInitiationErrorAction =
  | "reload_configuration"
  | "stop_for_user_action"
  | "recover_authoritatively";

export type ListingPaymentInitiationErrorResolution<TSession> = {
  action: ListingPaymentInitiationErrorAction;
  disposition: ListingPaymentInitiationErrorDisposition;
  session: TSession;
  uiState: ListingPaymentInitiationErrorUiState;
  allowsNewPaymentKey: false;
  requiresManualReview: false;
};

const DETERMINISTIC_REJECTION_CODES = new Set([
  "failed-precondition",
  "invalid-argument",
  "permission-denied",
  "unauthenticated",
]);

export const LISTING_PAYMENT_INITIATION_MESSAGES = Object.freeze({
  configuration_error:
    "Payment details changed. Review the refreshed amount before retrying the saved request.",
  initiation_rejected:
    "The payment request was not started. Review your payment details and account requirements before retrying the saved request.",
  authentication_required:
    "Your sign-in must be refreshed before payment can continue. Refresh your sign-in, then check the saved request; do not start a new payment.",
  permission_denied:
    "This account is not allowed to start the saved payment request. Do not start another payment; check the signed-in account or contact support.",
  initiation_error:
    "The result of the payment request is uncertain. Check the saved request status; do not start a second payment.",
});

export function listingPaymentFunctionErrorCode(error: unknown): string {
  if (!error || typeof error !== "object" || !("code" in error)) return "";

  const rawCode = String((error as { code?: unknown }).code ?? "")
    .trim()
    .toLowerCase();

  return rawCode.includes("/")
    ? rawCode.slice(rawCode.lastIndexOf("/") + 1)
    : rawCode;
}

export function isStalePaymentConfigurationError(error: unknown) {
  if (!error || typeof error !== "object") return false;

  const message = String(
    "message" in error
      ? (error as { message?: unknown }).message ?? ""
      : ""
  );

  return (
    listingPaymentFunctionErrorCode(error) === "failed-precondition" &&
    message.includes(STALE_PAYMENT_CONFIGURATION_MESSAGE)
  );
}

export function classifyListingPaymentInitiationError(
  error: unknown
): ListingPaymentInitiationErrorDisposition {
  if (isStalePaymentConfigurationError(error)) return "stale_configuration";

  return DETERMINISTIC_REJECTION_CODES.has(
    listingPaymentFunctionErrorCode(error)
  )
    ? "deterministic_rejection"
    : "recover_authoritatively";
}

export function resolveListingPaymentInitiationError<TSession>(
  error: unknown,
  session: TSession
): ListingPaymentInitiationErrorResolution<TSession> {
  const disposition = classifyListingPaymentInitiationError(error);

  if (disposition === "stale_configuration") {
    return {
      action: "reload_configuration",
      disposition,
      session,
      uiState: "configuration_error",
      allowsNewPaymentKey: false,
      requiresManualReview: false,
    };
  }

  if (disposition === "recover_authoritatively") {
    return {
      action: "recover_authoritatively",
      disposition,
      session,
      uiState: "initiation_error",
      allowsNewPaymentKey: false,
      requiresManualReview: false,
    };
  }

  const code = listingPaymentFunctionErrorCode(error);
  return {
    action: "stop_for_user_action",
    disposition,
    session,
    uiState:
      code === "unauthenticated"
        ? "authentication_required"
        : code === "permission-denied"
          ? "permission_denied"
          : "initiation_rejected",
    allowsNewPaymentKey: false,
    requiresManualReview: false,
  };
}

export type ListingPaymentRecoveryUiState =
  | "poll_timeout"
  | "initiation_error"
  | "failed"
  | "cancelled"
  | "timed_out"
  | "verification_required"
  | "creating_draft";

export function listingPaymentUiStateAfterAmbiguousRecovery(
  status:
    | "none"
    | "created"
    | "initiating"
    | "pending"
    | "succeeded"
    | "failed"
    | "cancelled"
    | "timed_out"
    | "verification_required"
    | "consumed"
): {
  uiState: ListingPaymentRecoveryUiState;
  allowsNewPaymentKey: false;
  requiresManualReview: boolean;
  manualStatusCheckEnabled: boolean;
} {
  if (status === "none") {
    return {
      uiState: "initiation_error",
      allowsNewPaymentKey: false,
      requiresManualReview: false,
      manualStatusCheckEnabled: true,
    };
  }
  if (
    status === "created" ||
    status === "initiating" ||
    status === "pending"
  ) {
    return {
      uiState: "poll_timeout",
      allowsNewPaymentKey: false,
      requiresManualReview: false,
      manualStatusCheckEnabled: true,
    };
  }
  if (status === "succeeded" || status === "consumed") {
    return {
      uiState: "creating_draft",
      allowsNewPaymentKey: false,
      requiresManualReview: false,
      manualStatusCheckEnabled: false,
    };
  }
  return {
    uiState: status,
    allowsNewPaymentKey: false,
    requiresManualReview: status === "verification_required",
    manualStatusCheckEnabled: false,
  };
}

export function malformedListingPaymentRecoveryState() {
  return {
    uiState: "initiation_error" as const,
    allowsNewPaymentKey: false as const,
    requiresManualReview: false as const,
  };
}

export const LISTING_PAYMENT_ONE_SHOT_STATUS_PATHS = [
  "ambiguous_initiation_recovery",
  "start_paid_payment_recovery",
  "prepare_terminal_retry",
  "retry_terminal_payment",
  "refresh_payment_status",
  "refresh_payment_session",
] as const;

export type ListingPaymentOneShotStatusPath =
  (typeof LISTING_PAYMENT_ONE_SHOT_STATUS_PATHS)[number];

export function beginOwnedListingPaymentPolling(args: {
  pollingTimer: ListingPaymentPollingTimer;
  windowMs: number;
  onTimeout: () => void;
  onPolling: (transition: { uiState: "polling"; run: number }) => void;
}): number {
  const run = args.pollingTimer.start({
    windowMs: args.windowMs,
    onTimeout: args.onTimeout,
  });

  if (!args.pollingTimer.isCurrent(run)) {
    throw new Error("Polling UI requires an owned automatic polling cycle.");
  }

  args.onPolling({ uiState: "polling", run });
  return run;
}

export function stopOwnedListingPaymentPolling(args: {
  pollingTimer: ListingPaymentPollingTimer;
  activePayment: boolean;
  onActiveStopped: (transition: { uiState: "poll_timeout" }) => void;
}): void {
  args.pollingTimer.stop();
  if (args.activePayment) {
    args.onActiveStopped({ uiState: "poll_timeout" });
  }
}

export async function processOneShotListingPaymentStatus<TSession>(args: {
  path: ListingPaymentOneShotStatusPath;
  processStatus: () => Promise<TSession | null>;
  onActive: (transition: {
    path: ListingPaymentOneShotStatusPath;
    uiState: "poll_timeout";
    session: TSession;
  }) => void;
}): Promise<TSession | null> {
  const session = await args.processStatus();

  if (session) {
    args.onActive({
      path: args.path,
      uiState: "poll_timeout",
      session,
    });
  }

  return session;
}

export async function runOwnedPaymentAuthenticationRefresh(args: {
  refreshToken: () => Promise<unknown>;
  isOwnerCurrent: () => boolean;
  onSuccess: () => void;
  onFailure: (error: unknown) => void;
  onSettled: () => void;
}): Promise<"succeeded" | "failed" | "stale"> {
  try {
    await args.refreshToken();
    if (!args.isOwnerCurrent()) return "stale";
    args.onSuccess();
    return "succeeded";
  } catch (error) {
    if (!args.isOwnerCurrent()) return "stale";
    args.onFailure(error);
    return "failed";
  } finally {
    if (args.isOwnerCurrent()) {
      args.onSettled();
    }
  }
}
